// testing/ParallaxV6TestingScreen.jsx
// ─────────────────────────────────────────────────────────────────────────────
// DeepReal Verification — Production flow (UX-B, sweep-guided, cleaned)
//
// Cleanup pass on top of the validated harness:
//   • Algorithm + NCC tracking + sweep state machine: UNCHANGED, bit-for-bit
//   • Removed: test category selector, hook live state panel, aggregator panel,
//     terminal raw card, technical metrics, Export JSON, [TESTING] logs,
//     phase badge "STREAMING:", best-frame diagnostics thumbnail
//   • Removed video overlays: edge bars, sweep progress bar, directional arrow,
//     sweep counter R/L, half-sweep flash
//   • Kept video overlays: movement quality warning, "Hold still" post-sweep
//   • Added: translation instruction (static), direction hint (live, below video),
//     user-facing verdict, standalone subject confirmation card, UploadMintPanel
//
// ROLLBACK: copy this file from D:\DeepReal\9_Parallax_Testing_Claude\client\src\testing\
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useRef, useState } from "react";
import DeviceCheck from "../components/DeviceCheck";
import AnalyzeDepthFromFrames from "../components/AnalyzeDepthFromFrames";
import UploadMintPanel from "../components/UploadMintPanel";
import useStreamingParallax from "../experiments/parallax-streaming/useStreamingParallax";
import { useCamera } from "../context/CameraContext";
import { moireScore, estimateRefresh, blueRatio, decideScreenLike } from "../utils/screenDetection";

const PHASE_COLOR = {
  idle:    "#64748b",
  running: "#38bdf8",
  pass:    "#22c55e",
  fail:    "#ef4444",
  retry:   "#f59e0b",
  error:   "#ef4444",
};

const TERMINAL_PHASES = new Set(["pass", "fail", "retry", "error"]);

const N_SWEEPS_REQUIRED = 2;
const DEBUG_PREFIX = "[DeepRealDebug][Screen]";

// ─── StreamingParallaxController ─────────────────────────────────────────────
const StreamingParallaxController = ({ videoRef, enabled, onStartReady, onHookUpdate }) => {
  const hookResult = useStreamingParallax({
    enabled,
    videoElementRef: videoRef,
    config: {
      frameIntervalMs: 250,
      targetFrames: 24,
      maxDurationMs: 60000,
      motionGate: {
        minDeltaDeg: 2.0,
        maxWaitMs:   900,
        axis:        'gamma',
        minCaptureIntervalMs: 250,
      },
    },
  });

  useEffect(() => {
    onStartReady?.(hookResult.start);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onHookUpdate?.(hookResult);
  }, [hookResult.phase, hookResult.progress, hookResult.confidence]); // eslint-disable-line

  useEffect(() => {
    if (!enabled) return;
    hookResult.start();
  }, [enabled]); // eslint-disable-line

  return null;
};

// ─── ParallaxV6TestingScreen ──────────────────────────────────────────────────
const ParallaxV6TestingScreen = () => {
  const [flowStarted,       setFlowStarted]       = useState(false);
  const [deviceCheckResult, setDeviceCheckResult] = useState(null);
  const [streamingEnabled,  setStreamingEnabled]  = useState(false);
  const [hookSnapshot,      setHookSnapshot]      = useState(null);
  const [sceneLocked,       setSceneLocked]       = useState(false);
  const [challengePhase,    setChallengePhase]    = useState("idle");
  const [parallaxStarted,   setParallaxStarted]   = useState(false);
  const [sweepRightDone,    setSweepRightDone]    = useState(false);
  const [sweepLeftDone,     setSweepLeftDone]     = useState(false);
  const [sweepCount,        setSweepCount]        = useState(0);

  const [lockFrame,        setLockFrame]        = useState(null);
  const [subjectConfirmed, setSubjectConfirmed] = useState(false);
  // Lock-time screen check — blocks lock if decideScreenLike returns isScreen
  const [initialScreenBlocked, setInitialScreenBlocked] = useState(false);

  // Rolling pre-lock frame buffer — captures {imageData, timestamp}
  const preLockScreenFramesRef = useRef([]);
  const preLockRafRef = useRef(null);
  const preLockCanvasRef = useRef(null);

  const { startCamera, setPrimaryVideoEl } = useCamera();
  const [isLandscape, setIsLandscape] = useState(() => window.innerWidth > window.innerHeight);

  const videoRef             = useRef(null);
  const startRef             = useRef(null);
  const referenceFrameRef    = useRef(null);
  const referenceROIRef      = useRef(null);
  const lockBaselineTestsRef = useRef(0);
  const patchRef             = useRef(null);
  const trackedOffsetRef     = useRef({ dx: 0, dy: 0 });
  const rafTrackingRef       = useRef(null);
  const trackingCanvasRef    = useRef(null);
  const sweepRef             = useRef({ rightDone: false, leftDone: false, count: 0 });
  const movementWarningRef   = useRef(null);
  const lastDirRef           = useRef(0);        // (+1=right, -1=left, 0=init)
  const boxOverlayRef        = useRef(null);     // kept null-safe (legacy)

  // NCC tracking diagnostic refs (used in rAF tick, no guard enforcement)
  const continuityLostCountRef   = useRef(0);
  const continuityTotalChecksRef = useRef(0);

  // Live refs for values consumed inside the rAF tick (avoids stale closures)
  const challengePhaseRef = useRef("idle");
  const progressPctRef    = useRef(0);
  const depthTestsRef     = useRef(0);

  useEffect(() => {
    if (!streamingEnabled) return;
    const initCamera = async () => {
      try {
        const stream = await startCamera();
        const videoEl = videoRef.current;
        if (!stream || !videoEl) return;
        if (videoEl.srcObject !== stream) {
          videoEl.muted = true; videoEl.playsInline = true; videoEl.autoplay = true;
          videoEl.srcObject = stream;
          try { await videoEl.play(); } catch (e) { /* autoplay */ }
          setPrimaryVideoEl(videoEl);
        }
      } catch (err) { console.error("[Camera] error:", err); }
    };
    initCamera();
  }, [streamingEnabled]); // eslint-disable-line

  // Landscape orientation support
  useEffect(() => {
    const update = () => setIsLandscape(window.innerWidth > window.innerHeight);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  // Keep live refs in sync with React state for rAF tick consumption
  useEffect(() => { challengePhaseRef.current = challengePhase; }, [challengePhase]);
  useEffect(() => { progressPctRef.current = Math.round((hookSnapshot?.progress ?? 0) * 100); }, [hookSnapshot?.progress]);
  useEffect(() => {
    const s = hookSnapshot?.summary;
    depthTestsRef.current = (Number(s?.positiveDepthTests) || 0) + (Number(s?.negativeDepthTests) || 0) + (Number(s?.invalidTests) || 0);
  }, [hookSnapshot?.summary?.positiveDepthTests, hookSnapshot?.summary?.negativeDepthTests, hookSnapshot?.summary?.invalidTests]);

  // ── Rolling pre-lock frame buffer ──────────────────────────────────────────
  // Continuously captures ImageData from the live video while the scene is unlocked.
  // When the user presses Lock Scene, the buffer already contains what the camera saw.
  const PRE_LOCK_BUFFER_SIZE = 10;
  const PRE_LOCK_INTERVAL_MS = 60;
  useEffect(() => {
    if (preLockRafRef.current) { clearInterval(preLockRafRef.current); preLockRafRef.current = null; }
    if (!streamingEnabled || sceneLocked) return;
    if (!preLockCanvasRef.current) {
      preLockCanvasRef.current = document.createElement('canvas');
      preLockCanvasRef.current.getContext('2d', { willReadFrequently: true });
    }
    const iv = setInterval(() => {
      const video = videoRef.current;
      if (!video || video.videoWidth <= 0 || video.videoHeight <= 0) return;
      const c = preLockCanvasRef.current;
      if (c.width !== video.videoWidth || c.height !== video.videoHeight) {
        c.width = video.videoWidth; c.height = video.videoHeight;
      }
      const ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(video, 0, 0, c.width, c.height);
      const img = ctx.getImageData(0, 0, c.width, c.height);
      const buf = preLockScreenFramesRef.current;
      buf.push({ imageData: img, timestamp: performance.now() });
      if (buf.length > PRE_LOCK_BUFFER_SIZE) buf.shift();
    }, PRE_LOCK_INTERVAL_MS);
    preLockRafRef.current = iv;
    return () => { clearInterval(iv); preLockRafRef.current = null; };
  }, [streamingEnabled, sceneLocked]);

  // Movement quality warnings (texts ≤15 chars)
  const MOVEMENT_WARNINGS = {
    too_fast:   { text: "\u26a1 Too fast",    color: "#fca5a5", border: "rgba(239,68,68,0.55)" },
    aim_closer: { text: "\u21d4 Wider sweep", color: "#fde68a", border: "rgba(251,191,36,0.55)" },
    good:       { text: "\u2713 Good",         color: "#86efac", border: "rgba(34,197,94,0.55)" },
  };

  useEffect(() => {
    const el = movementWarningRef.current;
    if (!el) return;
    const key = hookSnapshot?.movementWarning;
    const cfg = MOVEMENT_WARNINGS[key];
    if (!cfg) { el.style.opacity = "0"; return; }
    el.textContent = cfg.text;
    el.style.color = cfg.color;
    el.style.border = `1px solid ${cfg.border}`;
    el.style.opacity = "1";
  }, [hookSnapshot?.movementWarning]); // eslint-disable-line

  const handleStartFlow           = () => setFlowStarted(true);
  const handleDeviceCheckComplete = useCallback((ok) => setDeviceCheckResult(ok), []);
  const handleStartReady          = useCallback((fn) => { startRef.current = fn; }, []);
  const handleHookUpdate          = useCallback((result) => setHookSnapshot({ ...result }), []);
  const handleStartStreaming      = useCallback(() => setStreamingEnabled(true), []);

  const handleRetry = useCallback(() => {
    console.log('[DeepRealDebug][RetryReload]', {
      reason: 'user_retry',
      timestamp: new Date().toISOString(),
    });
    window.location.reload();
  }, []);

  const handleBlockScene = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.videoWidth <= 0 || video.videoHeight <= 0) {
      console.warn("[CHALLENGE] lock failed: video not ready"); return;
    }

    // ── Lock-time InitialScreenCheck (hard block: isScreen && score >= 0.6) ──
    const buf = preLockScreenFramesRef.current;
    if (buf.length >= 2) {
      const frames = buf.map(entry => entry.imageData);
      const mo = moireScore(frames);
      const rt = estimateRefresh(frames);
      const bl = blueRatio(frames[Math.floor(frames.length / 2)]);
      const decision = decideScreenLike({ moire: mo, refreshTurns: rt, blue: bl });
      console.log('[DeepRealDebug][InitialScreenCheck] result', {
        isScreen: decision.isScreen, score: decision.score,
        moire: mo, refreshTurns: rt, blue: bl,
        triggerReason: decision.triggerReason,
        bufferFrameCount: buf.length,
        timestamp: new Date().toISOString(),
      });
      if (decision.isScreen && decision.score >= 0.6) {
        console.warn('[DeepRealDebug][InitialScreenCheck] BLOCKED', {
          score: decision.score, triggerReason: decision.triggerReason,
          timestamp: new Date().toISOString(),
        });
        const dispCanvas = document.createElement("canvas");
        dispCanvas.width = video.videoWidth; dispCanvas.height = video.videoHeight;
        dispCanvas.getContext("2d").drawImage(video, 0, 0);
        setLockFrame(dispCanvas.toDataURL("image/jpeg", 0.85));
        setInitialScreenBlocked(true);
        setSceneLocked(true);
        setChallengePhase("idle");
        return;
      }
    } else {
      console.warn('[DeepRealDebug][InitialScreenCheck] skipped — insufficient buffer frames', {
        bufferFrameCount: buf.length, timestamp: new Date().toISOString(),
      });
    }

    // ── Normal lock proceeds ────────────────────────────────────────────────
    setInitialScreenBlocked(false);
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    referenceFrameRef.current = canvas.toDataURL("image/jpeg", 0.85);
    setLockFrame(referenceFrameRef.current);
    referenceROIRef.current = {
      x: Math.round(video.videoWidth * 0.1), y: Math.round(video.videoHeight * 0.1),
      width: Math.round(video.videoWidth * 0.8), height: Math.round(video.videoHeight * 0.8),
    };
    setSceneLocked(true); setParallaxStarted(true);
    lockBaselineTestsRef.current = 0; setChallengePhase("locked");

    const roi = referenceROIRef.current;
    const PATCH = 64;
    const pc = document.createElement("canvas"); pc.width = pc.height = PATCH;
    const pctx = pc.getContext("2d", { willReadFrequently: true });
    const cx = roi.x + roi.width / 2, cy = roi.y + roi.height / 2;
    pctx.drawImage(video, cx - PATCH/2, cy - PATCH/2, PATCH, PATCH, 0, 0, PATCH, PATCH);
    patchRef.current = pctx.getImageData(0, 0, PATCH, PATCH);
    trackedOffsetRef.current = { dx: 0, dy: 0 };
    continuityLostCountRef.current = 0;
    continuityTotalChecksRef.current = 0;
    setSweepRightDone(false); setSweepLeftDone(false); setSweepCount(0);
    sweepRef.current = { rightDone: false, leftDone: false, count: 0 };
    lastDirRef.current = 0;

    if (!trackingCanvasRef.current) {
      trackingCanvasRef.current = document.createElement("canvas");
      trackingCanvasRef.current.getContext("2d", { willReadFrequently: true });
    }
    trackingCanvasRef.current.width = video.videoWidth;
    trackingCanvasRef.current.height = video.videoHeight;

    console.log("[CHALLENGE] Scene locked ✅ | ROI:", referenceROIRef.current);
  }, []);

  // ── Derived UI values ─────────────────────────────────────────────────────
  const hookPhase   = hookSnapshot?.phase ?? "idle";
  const progressPct = Math.round((hookSnapshot?.progress ?? 0) * 100);

  const analysisPair = hookSnapshot?.analysisPair ?? null;
  const analysisKey  = hookSnapshot?.analysisKey  ?? 0;

  const uploadFrames  = hookSnapshot?.uploadFrames ?? null;
  const blockReason   = hookSnapshot?.blockReason  ?? "Verification not completed.";
  const hookCanUpload = hookSnapshot?.canUpload === true;

  // Phase: pass-through from hook, or retry if screen blocked at lock time
  const phase = initialScreenBlocked ? 'retry' : hookPhase;
  const phaseColor  = PHASE_COLOR[phase] ?? "#64748b";
  const canRetry    = phase === "fail" || phase === "retry" || phase === "error";
  const isTerminalPhase = TERMINAL_PHASES.has(phase);

  const canUpload = hookCanUpload && subjectConfirmed;

  const handleAnalysisComplete = useCallback((payload) => {
    hookSnapshot?.handleAnalysisComplete?.(payload);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hookSnapshot?.handleAnalysisComplete]);

  useEffect(() => {
    console.log(`${DEBUG_PREFIX} challengePhase updated`, {
      challengePhase,
      sweepCount,
      sceneLocked,
    });
  }, [challengePhase, sweepCount, sceneLocked]);

  useEffect(() => {
    console.log(`${DEBUG_PREFIX} hook snapshot`, {
      phase,
      progressPct,
      sweepCount,
      challengePhase,
      canUpload,
      subjectConfirmed,
      blockReason,
    });
  }, [phase, progressPct, sweepCount, challengePhase, canUpload, subjectConfirmed, blockReason]);

  // ── NCC + SweepGuide rAF loop ─────────────────────────────────────────────
  // ALGORITHM UNCHANGED — only the imperative DOM updates for removed overlays
  // (edge bars, sweep bar, arrow, half-sweep flash) have been deleted. The new
  // direction hint below the progress bar is updated here too.
  useEffect(() => {
    if (!sceneLocked || !streamingEnabled) {
      if (rafTrackingRef.current) { cancelAnimationFrame(rafTrackingRef.current); rafTrackingRef.current = null; }
      return;
    }

    if (!window.__deeprealTracking) {
      window.__deeprealTracking = {
        PATCH: 64, SEARCH_HALF: 32, NCC_MS: 100, MAX_DRIFT_PX: 250,
        LERP_ALPHA: 0.18, NCC_THRESHOLD: 0.5, SWEEP_THRESHOLD_RATIO: 0.16,
      };
    }

    const nccTargetRef = { dx: 0, dy: 0 };
    const lerpPosRef   = { dx: 0, dy: 0 };
    let lastNccTime = 0;

    const tick = (now) => {
      rafTrackingRef.current = requestAnimationFrame(tick);
      const cfg = window.__deeprealTracking;
      const PATCH        = cfg.PATCH        ?? 64;
      const SEARCH_HALF  = cfg.SEARCH_HALF  ?? 32;
      const NCC_MS       = cfg.NCC_MS       ?? 100;
      const MAX_DRIFT_PX = cfg.MAX_DRIFT_PX ?? 250;
      const LERP_ALPHA   = cfg.LERP_ALPHA   ?? 0.18;

      const video = videoRef.current, patch = patchRef.current;
      const roi = referenceROIRef.current, tCanvas = trackingCanvasRef.current;
      if (!video || !patch || !roi || !tCanvas) return;
      if (video.videoWidth <= 0 || video.videoHeight <= 0) return;
      const vw = video.videoWidth, vh = video.videoHeight;

      if (now - lastNccTime >= NCC_MS) {
        lastNccTime = now;
        if (tCanvas.width !== vw || tCanvas.height !== vh) { tCanvas.width = vw; tCanvas.height = vh; }
        const tCtx = tCanvas.getContext("2d", { willReadFrequently: true });
        tCtx.drawImage(video, 0, 0, vw, vh);
        const origCx = roi.x + roi.width / 2, origCy = roi.y + roi.height / 2;
        const sCx = origCx + nccTargetRef.dx, sCy = origCy + nccTargetRef.dy;
        const halfP = PATCH / 2;
        const xMin = Math.max(halfP, Math.round(sCx) - SEARCH_HALF);
        const xMax = Math.min(vw - halfP, Math.round(sCx) + SEARCH_HALF);
        const yMin = Math.max(halfP, Math.round(sCy) - SEARCH_HALF);
        const yMax = Math.min(vh - halfP, Math.round(sCy) + SEARCH_HALF);
        if (xMax - xMin >= 1 && yMax - yMin >= 1) {
          let bestScore = -Infinity, bestX = sCx, bestY = sCy;
          const pData = patch.data;
          for (let sy = yMin; sy <= yMax; sy += 8) {
            for (let sx = xMin; sx <= xMax; sx += 8) {
              const cx2 = Math.round(sx - halfP), cy2 = Math.round(sy - halfP);
              if (cx2 < 0 || cy2 < 0 || cx2 + PATCH > vw || cy2 + PATCH > vh) continue;
              const cData = tCtx.getImageData(cx2, cy2, PATCH, PATCH).data;
              let sP=0,sC=0,sPP=0,sCC=0,sPC=0;
              const len = PATCH * PATCH;
              for (let i = 0; i < len; i++) {
                const i4=i*4, p=pData[i4]*.299+pData[i4+1]*.587+pData[i4+2]*.114;
                const c=cData[i4]*.299+cData[i4+1]*.587+cData[i4+2]*.114;
                sP+=p; sC+=c; sPP+=p*p; sCC+=c*c; sPC+=p*c;
              }
              const mP=sP/len, mC=sC/len, num=sPC-len*mP*mC;
              const den=Math.sqrt(Math.max(0,sPP-len*mP*mP)*Math.max(0,sCC-len*mC*mC));
              const ncc=den<1e-6?0:num/den;
              if (ncc > bestScore) { bestScore=ncc; bestX=sx; bestY=sy; }
            }
          }
          const nccThreshold = cfg.NCC_THRESHOLD ?? 0.5;
          const HARD_NCC_THRESHOLD = 0.35;
          const CONTINUITY_LOST_REQUIRED = 4;
          const ndx = bestX - origCx, ndy = bestY - origCy;
          const drift = Math.max(Math.abs(ndx), Math.abs(ndy));
          const nccOk = bestScore >= nccThreshold;
          const driftOk = drift <= MAX_DRIFT_PX;

          if (nccOk && driftOk) {
            nccTargetRef.dx=ndx; nccTargetRef.dy=ndy;
            trackedOffsetRef.current={dx:ndx,dy:ndy};
            continuityLostCountRef.current = 0;
            continuityTotalChecksRef.current += 1;
          } else {
            if (nccOk && !driftOk) {
              nccTargetRef.dx=0; nccTargetRef.dy=0;
              trackedOffsetRef.current={dx:0,dy:0};
            }
            continuityTotalChecksRef.current += 1;
            const hardNccLoss = bestScore < HARD_NCC_THRESHOLD;
            const driftLoss = nccOk && !driftOk;
            if (hardNccLoss || driftLoss) {
              continuityLostCountRef.current += 1;
            } else {
              continuityLostCountRef.current = 0;
            }
          }
        }
      }

      lerpPosRef.dx += (nccTargetRef.dx - lerpPosRef.dx) * LERP_ALPHA;
      lerpPosRef.dy += (nccTargetRef.dy - lerpPosRef.dy) * LERP_ALPHA;

      // SweepGuide — direction tracking + half-sweep counting (UNCHANGED logic)
      const SWEEP_THRESHOLD = (cfg.SWEEP_THRESHOLD_RATIO ?? 0.20) * vw;
      const curDir = lerpPosRef.dx >= 0 ? 1 : -1;
      lastDirRef.current = curDir;

      // Half-sweep detection (right)
      if (lerpPosRef.dx <= -SWEEP_THRESHOLD && !sweepRef.current.rightDone) {
        sweepRef.current.rightDone = true; setSweepRightDone(true);
        console.log("[SWEEP] right done dx=", lerpPosRef.dx.toFixed(1));
      }
      // Half-sweep detection (left)
      if (lerpPosRef.dx >= SWEEP_THRESHOLD && !sweepRef.current.leftDone) {
        sweepRef.current.leftDone = true; setSweepLeftDone(true);
        console.log("[SWEEP] left done dx=", lerpPosRef.dx.toFixed(1));
      }
      // Full bilateral sweep completed
      if (sweepRef.current.rightDone && sweepRef.current.leftDone) {
        const nc = sweepRef.current.count + 1;
        sweepRef.current = { rightDone: false, leftDone: false, count: nc };
        setSweepCount(nc); setSweepRightDone(false); setSweepLeftDone(false);
        lastDirRef.current = 0;
        console.log("[SWEEP] bilateral #", nc);
      }
    };

    rafTrackingRef.current = requestAnimationFrame(tick);
    return () => { if (rafTrackingRef.current) { cancelAnimationFrame(rafTrackingRef.current); rafTrackingRef.current=null; } };
  }, [sceneLocked, streamingEnabled]); // eslint-disable-line

  // ── Challenge phase transitions ───────────────────────────────────────────
  useEffect(() => {
    if (!streamingEnabled || phase !== "running" || !sceneLocked) return;
    if (challengePhase === "validated" || challengePhase === "invalid") return;
    const summary = hookSnapshot?.summary; if (!summary) return;
    const tsl = (summary.numberOfTests ?? 0) - lockBaselineTestsRef.current;
    const motStdDev = summary.motionStdDev ?? 0;
    const hasDynamic = summary.hasDynamicWindow === true;
    const positiveDepthTests = summary.positiveDepthTests ?? 0;
    const negativeDepthTests = summary.negativeDepthTests ?? 0;
    if (challengePhase === "locked" && tsl >= 1 && (motStdDev > 0 || hasDynamic)) { setChallengePhase("moving"); return; }
    if (challengePhase === "moving" && sweepCount >= N_SWEEPS_REQUIRED) { setChallengePhase("validated"); console.log("[CHALLENGE] validated ✅"); return; }
    if (challengePhase === "moving" && tsl >= 14 && sweepCount === 0 && positiveDepthTests < 4 && negativeDepthTests === 0) { setChallengePhase("invalid"); return; }
    if (challengePhase === "locked" && tsl >= 5 && motStdDev === 0 && !hasDynamic) { setChallengePhase("invalid"); }
  }, [streamingEnabled, phase, sceneLocked, challengePhase, sweepCount,
    hookSnapshot?.summary?.numberOfTests, hookSnapshot?.summary?.motionStdDev,
    hookSnapshot?.summary?.hasDynamicWindow, hookSnapshot?.summary?.positiveDepthTests,
    hookSnapshot?.summary?.negativeDepthTests]); // eslint-disable-line

  // User-facing verdict (no raw phase names).
  // Differentiates "insufficient movement" from "flat/screen" using motion metrics:
  // when motionStdDev is near-zero AND no screen-like signals were seen, the failure
  // is almost certainly due to the user not moving the phone enough — not a flat scene.
  const getVerdict = () => {
    // NOTE: screenRiskDetected è soft warning, non altera il verdetto (STEP 13 v2)
    if (phase === "pass" && challengePhase === "validated")
      return { label: "Authentic scene verified", color: "#86efac" };
    if (phase === "pass")
      return { label: "Depth verified", color: "#86efac" };
    if (phase === "fail") {
      const screenLikeSignals = hookSnapshot?.summary?.screenLikeSignals ?? 0;
      if (screenLikeSignals > 0) {
        return {
          label: "Could not confirm a 3D scene. This can be a flat surface, screen, or photo. Please retry.",
          color: "#fca5a5",
        };
      }
      return {
        label: "Could not confirm stable 3D depth from the captured motion. Please retry with a wider, smoother sweep.",
        color: "#fca5a5",
      };
    }
    if (phase === "retry" && initialScreenBlocked)
      return {
        label: "Screen detected at lock time — please lock a real subject.",
        color: "#fcd34d",
      };
    if (phase === "retry")
      return {
        label: "Could not verify depth. Move more slowly and retry. If the subject is a flat surface, screen or photo, this is the expected result.",
        color: "#fcd34d",
      };
    if (phase === "error")
      return { label: "Verification error \u2014 please retry", color: "#fca5a5" };
    return null;
  };
  const verdict = getVerdict();

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="notranslate" translate="no" style={{ minHeight:"60vh", display:"flex", flexDirection:"column",
      alignItems:"center", justifyContent:"center", color:"#e2e8f0", padding:"2rem", gap:"1rem" }}>

      <h2 style={{ fontSize:"1.6rem", fontWeight:700, margin:0, textAlign:"center" }}>
        DeepReal Images
      </h2>
      <p style={{
        fontSize:"0.88rem", color:"#94a3b8", margin:0,
        textAlign:"center", maxWidth:480,
      }}>
        Capture real-world content with immutable proof of authenticity.
      </p>

      {/* Start button */}
      {!flowStarted && (
        <button type="button" onClick={handleStartFlow}
          style={{ padding:"14px 36px", background:"rgba(34,197,94,.18)", border:"1px solid #22c55e", borderRadius:8, color:"#86efac", fontSize:"1.05rem", fontWeight:700, cursor:"pointer" }}
          onMouseEnter={e=>e.currentTarget.style.background="rgba(34,197,94,.28)"}
          onMouseLeave={e=>e.currentTarget.style.background="rgba(34,197,94,.18)"}>
          ▶ Start Verification
        </button>
      )}

      {/* Device check stage — kept mounted to surface per-check status */}
      {flowStarted && (
        <div style={{ width:"100%", maxWidth:480, background:"rgba(15,23,42,.55)", border:"1px solid #334155", borderRadius:8, padding:"1rem 1.5rem" }}>
          <p style={{ fontSize:"0.75rem", color:"#64748b", margin:"0 0 8px 0", textTransform:"uppercase", letterSpacing:"0.06em" }}>Device compatibility</p>
          <DeviceCheck onCheckComplete={handleDeviceCheckComplete} />
        </div>
      )}

      {deviceCheckResult === false && (
        <div style={{ width:"100%", maxWidth:480, padding:"12px 16px", borderRadius:8,
          border:"1px solid #ef4444", background:"rgba(239,68,68,.12)", color:"#fca5a5" }}>
          <strong>One or more checks failed.</strong>
          <p style={{ fontSize:"0.82rem", margin:"6px 0 0 0" }}>
            Review the list above. If this is a sensor/camera permission issue, allow access and reload the page.
          </p>
        </div>
      )}

      {/* Streaming controller (headless) */}
      {deviceCheckResult === true && (
        <StreamingParallaxController videoRef={videoRef} enabled={parallaxStarted}
          onStartReady={handleStartReady} onHookUpdate={handleHookUpdate} />
      )}

      {/* Hidden analysis */}
      {analysisPair && (
        <div style={{ display:"none" }}>
          <AnalyzeDepthFromFrames key={analysisKey}
            frame1={analysisPair.frame1} frame2={analysisPair.frame2}
            orientations={analysisPair.orientations} onAnalysisComplete={handleAnalysisComplete} />
        </div>
      )}

      {/* Start camera button */}
      {deviceCheckResult === true && !streamingEnabled && (
        <button type="button" onClick={handleStartStreaming}
          style={{ padding:"14px 36px", background:"rgba(56,189,248,.15)", border:"1px solid #38bdf8", borderRadius:8, color:"#7dd3fc", fontSize:"1.05rem", fontWeight:700, cursor:"pointer" }}
          onMouseEnter={e=>e.currentTarget.style.background="rgba(56,189,248,.28)"}
          onMouseLeave={e=>e.currentTarget.style.background="rgba(56,189,248,.15)"}>
          ▶ Start Camera
        </button>
      )}

      {/* Video feed — minimal overlays only */}
      {streamingEnabled && (
        <div style={{ width:"100%", maxWidth:520, borderRadius:10, overflow:"hidden",
          border:`1px solid ${phaseColor}`, background:"#000",
          aspectRatio: isLandscape ? "4/3" : "3/4",
          transition:"border-color 300ms ease, aspect-ratio 200ms ease", position:"relative" }}>
          <video ref={videoRef} autoPlay playsInline muted
            style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }} />

          {phase === "running" && sceneLocked && (
            <div style={{ position:"absolute", inset:0, pointerEvents:"none" }}>

              {/* Movement quality warning (centre-top of feed) */}
              <div ref={movementWarningRef} style={{
                position:"absolute", bottom:"30%", left:"50%", transform:"translateX(-50%)",
                background:"rgba(15,23,42,0.88)", fontSize:"0.78rem", fontWeight:700,
                padding:"6px 18px", borderRadius:20, border:"1px solid transparent",
                whiteSpace:"nowrap", opacity:0, transition:"opacity 220ms ease",
                pointerEvents:"none", zIndex:10,
              }} />

              {/* Post-sweep "Hold still" instruction */}
              {sweepCount >= N_SWEEPS_REQUIRED && phase === "running" && (
                <div style={{
                  position:"absolute", bottom:"10%", left:"50%", transform:"translateX(-50%)",
                  background:"rgba(15,23,42,0.82)", color:"#93c5fd",
                  fontSize:"0.8rem", fontWeight:600, padding:"5px 16px", borderRadius:16,
                  border:"1px solid rgba(96,165,250,0.35)", whiteSpace:"nowrap",
                }}>
                  📷 Hold still...
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Translation instruction (static, visible when locked & not terminal) */}
      {streamingEnabled && sceneLocked && !isTerminalPhase && (
        <div style={{
          width:"100%", maxWidth:480, padding:"10px 14px", borderRadius:8,
          border:"1px solid rgba(148,163,184,0.25)", background:"rgba(15,23,42,0.5)",
          color:"#cbd5e1", fontSize:"0.82rem", lineHeight:1.5, textAlign:"center",
        }}>
          Hold your phone vertically and slide it sideways — left and right — keeping the camera pointed at the subject. Don't tilt or rotate.
        </div>
      )}

      {/* Lock subject button (only before parallax starts) */}
      {streamingEnabled && !parallaxStarted && !isTerminalPhase && (
        <div style={{ width:"100%", maxWidth:480, display:"flex", flexDirection:"column", gap:"0.4rem" }}>
          <button type="button" onClick={handleBlockScene} style={{
            padding:"11px 28px",
            background:sceneLocked?"rgba(168,85,247,.18)":"rgba(139,92,246,.15)",
            border:`1px solid ${sceneLocked?"#a855f7":"#7c3aed"}`,
            borderRadius:8, color:sceneLocked?"#d8b4fe":"#c4b5fd",
            fontSize:"0.95rem", fontWeight:700,
            cursor:sceneLocked?"default":"pointer", transition:"all 200ms ease", width:"100%",
          }}>
            {sceneLocked ? "🔒 Scene locked" : "🔒 Lock subject"}
          </button>
          {!sceneLocked && (
            <p style={{ margin:0, fontSize:"0.74rem", color:"#475569", textAlign:"center" }}>
              Best results with subjects within 5 metres
            </p>
          )}
        </div>
      )}

      {/* Screen-like signal banner removed in STEP 13 v4 — moireScore produces false
           positives on real scenes; detector kept as console diagnostic only. */}

      {/* Verification progress bar */}
      {streamingEnabled && (
        <div style={{ width:"100%", maxWidth:480 }}>
          <div style={{
            display:"flex", justifyContent:"space-between",
            fontSize:"0.8rem", color:"#94a3b8", marginBottom:4,
          }}>
            <span>Verification progress</span>
            <span>{progressPct}%</span>
          </div>
          <div style={{ width:"100%", height:8, borderRadius:999, background:"rgba(100,116,139,.3)", overflow:"hidden" }}>
            <div style={{
              width:`${progressPct}%`, height:"100%",
              background:phaseColor, borderRadius:999,
              transition:"width 150ms linear, background 300ms ease",
            }} />
          </div>
        </div>
      )}

      {/* Progress-based hint (text reflects how much of the verification work is done) */}
      {streamingEnabled && sceneLocked && !isTerminalPhase && (() => {
        let hintText, hintColor;
        if (progressPct >= 100) {
          hintText  = "Analyzing \u2014 hold still...";
          hintColor = "#86efac";
        } else if (progressPct >= 71) {
          hintText  = "Almost done \u2014 keep going (" + progressPct + "%)";
          hintColor = "#86efac";
        } else if (progressPct >= 31) {
          hintText  = "Keep moving \u2014 almost there (" + progressPct + "%)";
          hintColor = "#cbd5e1";
        } else if (progressPct >= 1) {
          hintText  = "Keep moving \u2014 early stage (" + progressPct + "%)";
          hintColor = "#cbd5e1";
        } else {
          hintText  = "Keep moving the phone left and right";
          hintColor = "#cbd5e1";
        }
        return (
          <div style={{
            width:"100%", maxWidth:480, textAlign:"center",
            fontSize:"0.95rem", fontWeight:700, color: hintColor,
            padding:"8px 14px", borderRadius:8,
            background:"rgba(15,23,42,0.55)", border:"1px solid rgba(148,163,184,0.25)",
            transition:"color 200ms ease",
          }}>
            {hintText}
          </div>
        );
      })()}

      {/* Verdict callout */}
      {verdict && isTerminalPhase && (
        <div style={{
          width:"100%", maxWidth:480, padding:"12px 18px", borderRadius:10,
          border:`1px solid ${phaseColor}`, background:"rgba(15,23,42,0.72)",
          color:verdict.color, fontSize:"0.95rem", fontWeight:700, textAlign:"center",
        }}>
          {verdict.label}
        </div>
      )}

      {/* Subject confirmation (on pass) */}
      {phase === "pass" && lockFrame && (
        <div style={{
          width:"100%", maxWidth:480, background:"rgba(15,23,42,.55)",
          border:"1px solid #334155", borderRadius:10, padding:"1rem 1.25rem",
        }}>
          <p style={{ fontSize:"0.82rem", color:"#cbd5e1", margin:"0 0 8px 0" }}>
            Confirm this is the subject you want to authenticate:
          </p>
          <img src={lockFrame} alt="Subject" style={{
            width:"100%", borderRadius:6, display:"block",
            border:subjectConfirmed
              ? "2px solid rgba(34,197,94,0.85)"
              : "1px solid rgba(148,163,184,0.35)",
            transition:"border 200ms ease",
          }} />
          {!subjectConfirmed ? (
            <div style={{ display:"flex", gap:"0.5rem", marginTop:"0.65rem" }}>
              <button type="button" onClick={() => setSubjectConfirmed(true)} style={{
                flex:1, padding:"10px 0",
                background:"rgba(34,197,94,0.15)", border:"1px solid #22c55e",
                borderRadius:8, color:"#86efac",
                fontSize:"0.92rem", fontWeight:700, cursor:"pointer",
              }}>
                ✅ Confirm subject
              </button>
              <button type="button" onClick={handleRetry} style={{
                flex:1, padding:"10px 0",
                background:"rgba(245,158,11,0.12)", border:"1px solid #f59e0b",
                borderRadius:8, color:"#fcd34d",
                fontSize:"0.92rem", fontWeight:700, cursor:"pointer",
              }}>
                🔄 Retry
              </button>
            </div>
          ) : (
            <p style={{
              fontSize:"0.82rem", color:"#86efac", margin:"10px 0 0 0",
              fontWeight:600, textAlign:"center",
            }}>
              ✅ Subject confirmed — ready to mint.
            </p>
          )}
        </div>
      )}

      {/* Retry button for non-pass terminal states */}
      {canRetry && phase !== "pass" && (
        <button type="button" onClick={handleRetry} style={{
          padding:"10px 28px", background:"rgba(245,158,11,.15)",
          border:"1px solid #f59e0b", borderRadius:8, color:"#fcd34d",
          fontSize:"0.95rem", fontWeight:700, cursor:"pointer",
        }}
          onMouseEnter={e=>e.currentTarget.style.background="rgba(245,158,11,.28)"}
          onMouseLeave={e=>e.currentTarget.style.background="rgba(245,158,11,.15)"}>
          🔄 Retry
        </button>
      )}

      {/* Upload/Mint panel — only after subject confirmation */}
      {subjectConfirmed && phase === "pass" && (
        <div style={{ width:"100%", maxWidth:480 }}>
          <UploadMintPanel
            frames={uploadFrames}
            canUpload={canUpload}
            canMint={canUpload}
            blockReason={blockReason}
          />
        </div>
      )}

    </div>
  );
};

export default ParallaxV6TestingScreen;
